// ============================================================================
// BP-13-02: Admin Analytics Types
// BrightPath Platform - Admin Frontend
// ============================================================================

import { z } from 'zod';

// =============================================================================
// Time Period Enums
// =============================================================================

export const TimePeriodEnum = z.enum([
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'last_90_days',
  'this_month',
  'last_month',
  'this_year',
  'custom',
]);
export type TimePeriod = z.infer<typeof TimePeriodEnum>;

export const GranularityEnum = z.enum([
  'hour',
  'day',
  'week',
  'month',
]);
export type Granularity = z.infer<typeof GranularityEnum>;

// =============================================================================
// Analytics Data Points
// =============================================================================

export const TimeSeriesDataPointSchema = z.object({
  timestamp: z.string().datetime(),
  value: z.number(),
  label: z.string().optional(),
});
export type TimeSeriesDataPoint = z.infer<typeof TimeSeriesDataPointSchema>;

export const CategoryDataPointSchema = z.object({
  category: z.string(),
  value: z.number(),
  percentage: z.number(),
  color: z.string().optional(),
});
export type CategoryDataPoint = z.infer<typeof CategoryDataPointSchema>;

// =============================================================================
// User Analytics
// =============================================================================

export const UserAnalyticsSchema = z.object({
  period: TimePeriodEnum,
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  
  // Summary metrics
  totalUsers: z.number(),
  newUsers: z.number(),
  activeUsers: z.number(),
  returningUsers: z.number(),
  churnedUsers: z.number(),
  
  // Growth
  userGrowthRate: z.number(), // Percentage
  userGrowthTrend: z.enum(['up', 'down', 'stable']),
  
  // Time series
  newUsersTrend: z.array(TimeSeriesDataPointSchema),
  activeUsersTrend: z.array(TimeSeriesDataPointSchema),
  
  // Breakdowns
  usersByRole: z.array(CategoryDataPointSchema),
  usersByVerificationStatus: z.array(CategoryDataPointSchema),
  usersByPlatform: z.array(CategoryDataPointSchema),
  
  // Engagement
  averageSessionDuration: z.number(), // seconds
  averageSessionsPerUser: z.number(),
  bounceRate: z.number(), // percentage
});
export type UserAnalytics = z.infer<typeof UserAnalyticsSchema>;

// =============================================================================
// Engagement Analytics
// =============================================================================

export const EngagementAnalyticsSchema = z.object({
  period: TimePeriodEnum,
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  
  // Content engagement
  totalPageViews: z.number(),
  uniquePageViews: z.number(),
  averagePagesPerSession: z.number(),
  
  // Feature usage
  featureUsage: z.array(z.object({
    feature: z.string(),
    usageCount: z.number(),
    uniqueUsers: z.number(),
    trend: z.enum(['up', 'down', 'stable']),
  })),
  
  // Document activity
  documentsUploaded: z.number(),
  documentsViewed: z.number(),
  documentsShared: z.number(),
  
  // Health tracking
  healthLogsCreated: z.number(),
  medicationsTracked: z.number(),
  symptomsLogged: z.number(),
  
  // Appointments
  appointmentsScheduled: z.number(),
  appointmentsCompleted: z.number(),
  appointmentsCancelled: z.number(),
  
  // Community
  forumPostsCreated: z.number(),
  forumReplies: z.number(),
  mentorMatches: z.number(),
  supportGroupJoins: z.number(),
  
  // Time series
  engagementTrend: z.array(TimeSeriesDataPointSchema),
  topPages: z.array(z.object({
    path: z.string(),
    title: z.string(),
    views: z.number(),
  })),
});
export type EngagementAnalytics = z.infer<typeof EngagementAnalyticsSchema>;

// =============================================================================
// Content Analytics
// =============================================================================

export const ContentAnalyticsSchema = z.object({
  period: TimePeriodEnum,
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  
  // Resources
  totalResources: z.number(),
  publishedResources: z.number(),
  draftResources: z.number(),
  resourceViews: z.number(),
  resourceSaves: z.number(),
  averageResourceRating: z.number(),
  
  // By category
  resourcesByCategory: z.array(CategoryDataPointSchema),
  
  // Top content
  topResources: z.array(z.object({
    id: z.string().uuid(),
    title: z.string(),
    views: z.number(),
    saves: z.number(),
    rating: z.number(),
  })),
  
  // Forum stats
  totalForumPosts: z.number(),
  activeForumUsers: z.number(),
  averageRepliesPerPost: z.number(),
  forumPostsByCategory: z.array(CategoryDataPointSchema),
  
  // Reviews
  totalReviews: z.number(),
  averageReviewRating: z.number(),
  reviewsByRating: z.array(CategoryDataPointSchema),
});
export type ContentAnalytics = z.infer<typeof ContentAnalyticsSchema>;

// =============================================================================
// Marketplace Analytics
// =============================================================================

export const MarketplaceAnalyticsSchema = z.object({
  period: TimePeriodEnum,
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  
  // Vendors
  totalVendors: z.number(),
  activeVendors: z.number(),
  newVendors: z.number(),
  pendingVerifications: z.number(),
  
  // Products
  totalProducts: z.number(),
  activeProducts: z.number(),
  newProducts: z.number(),
  
  // Activity
  productViews: z.number(),
  inquiries: z.number(),
  quoteRequests: z.number(),
  
  // Reviews
  totalProductReviews: z.number(),
  averageProductRating: z.number(),
  
  // Categories
  vendorsByCategory: z.array(CategoryDataPointSchema),
  productsByCategory: z.array(CategoryDataPointSchema),
  
  // Top performers
  topVendors: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    productCount: z.number(),
    reviewCount: z.number(),
    averageRating: z.number(),
  })),
  
  topProducts: z.array(z.object({
    id: z.string().uuid(),
    name: z.string(),
    vendorName: z.string(),
    views: z.number(),
    inquiries: z.number(),
  })),
  
  // Trends
  vendorGrowthTrend: z.array(TimeSeriesDataPointSchema),
  inquiryTrend: z.array(TimeSeriesDataPointSchema),
});
export type MarketplaceAnalytics = z.infer<typeof MarketplaceAnalyticsSchema>;

// =============================================================================
// System Analytics
// =============================================================================

export const SystemHealthSchema = z.object({
  status: z.enum(['healthy', 'degraded', 'down']),
  uptime: z.number(), // percentage
  uptimeString: z.string(), // "99.99%"
  
  // Services
  services: z.array(z.object({
    name: z.string(),
    status: z.enum(['healthy', 'degraded', 'down']),
    responseTime: z.number(), // ms
    lastCheck: z.string().datetime(),
    message: z.string().optional(),
  })),
  
  // Database
  database: z.object({
    status: z.enum(['healthy', 'degraded', 'down']),
    connectionPoolUsage: z.number(), // percentage
    queryLatency: z.number(), // ms
    activeConnections: z.number(),
    maxConnections: z.number(),
  }),
  
  // Cache
  cache: z.object({
    status: z.enum(['healthy', 'degraded', 'down']),
    hitRate: z.number(), // percentage
    memoryUsage: z.number(), // percentage
    keys: z.number(),
  }),
  
  // Storage
  storage: z.object({
    status: z.enum(['healthy', 'degraded', 'down']),
    usedSpace: z.number(), // bytes
    totalSpace: z.number(), // bytes
    usagePercentage: z.number(),
    documentsCount: z.number(),
  }),
  
  // Background jobs
  backgroundJobs: z.object({
    pending: z.number(),
    processing: z.number(),
    failed: z.number(),
    completed24h: z.number(),
    averageProcessingTime: z.number(), // ms
  }),
  
  // Performance
  performance: z.object({
    averageResponseTime: z.number(), // ms
    requestsPerMinute: z.number(),
    errorRate: z.number(), // percentage
    p95ResponseTime: z.number(), // ms
    p99ResponseTime: z.number(), // ms
  }),
  
  lastUpdated: z.string().datetime(),
});
export type SystemHealth = z.infer<typeof SystemHealthSchema>;

// =============================================================================
// Audit Logs
// =============================================================================

export const AuditActionEnum = z.enum([
  'create',
  'read',
  'update',
  'delete',
  'login',
  'logout',
  'export',
  'import',
  'approve',
  'reject',
  'suspend',
  'activate',
  'permission_change',
  'settings_change',
  'other',
]);
export type AuditAction = z.infer<typeof AuditActionEnum>;

export const AuditResourceEnum = z.enum([
  'user',
  'family',
  'child',
  'document',
  'health_log',
  'appointment',
  'expense',
  'resource',
  'forum_post',
  'vendor',
  'product',
  'review',
  'notification',
  'settings',
  'system',
  'other',
]);
export type AuditResource = z.infer<typeof AuditResourceEnum>;

export const AuditLogSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  userName: z.string(),
  userEmail: z.string().email(),
  userRole: z.string(),
  action: AuditActionEnum,
  resource: AuditResourceEnum,
  resourceId: z.string().nullable(),
  resourceName: z.string().nullable(),
  description: z.string(),
  oldValue: z.record(z.unknown()).nullable(),
  newValue: z.record(z.unknown()).nullable(),
  ipAddress: z.string(),
  userAgent: z.string().nullable(),
  success: z.boolean(),
  errorMessage: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type AuditLog = z.infer<typeof AuditLogSchema>;

export const AuditLogListParamsSchema = z.object({
  page: z.number().min(1).default(1),
  pageSize: z.number().min(1).max(100).default(50),
  userId: z.string().uuid().optional(),
  action: AuditActionEnum.optional(),
  resource: AuditResourceEnum.optional(),
  success: z.boolean().optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
  search: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export type AuditLogListParams = z.infer<typeof AuditLogListParamsSchema>;

export const AuditLogListResponseSchema = z.object({
  items: z.array(AuditLogSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});
export type AuditLogListResponse = z.infer<typeof AuditLogListResponseSchema>;

// =============================================================================
// Dashboard Summary
// =============================================================================

export const AdminDashboardSummarySchema = z.object({
  // User stats
  totalUsers: z.number(),
  newUsersToday: z.number(),
  newUsersThisWeek: z.number(),
  activeUsersToday: z.number(),
  userGrowthTrend: z.enum(['up', 'down', 'stable']),
  userGrowthPercentage: z.number(),
  
  // Content stats
  totalFamilies: z.number(),
  totalChildren: z.number(),
  totalDocuments: z.number(),
  totalHealthLogs: z.number(),
  
  // Moderation
  pendingModerationItems: z.number(),
  pendingVendorVerifications: z.number(),
  flaggedContent: z.number(),
  
  // Marketplace
  totalVendors: z.number(),
  totalProducts: z.number(),
  
  // Community
  totalForumPosts: z.number(),
  activeForumUsers: z.number(),
  
  // System
  systemStatus: z.enum(['healthy', 'degraded', 'down']),
  failedJobsCount: z.number(),
  
  // Trends
  userTrend: z.array(TimeSeriesDataPointSchema),
  engagementTrend: z.array(TimeSeriesDataPointSchema),
  
  lastUpdated: z.string().datetime(),
});
export type AdminDashboardSummary = z.infer<typeof AdminDashboardSummarySchema>;

// =============================================================================
// Analytics Request
// =============================================================================

export const AnalyticsRequestSchema = z.object({
  period: TimePeriodEnum.default('last_30_days'),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  granularity: GranularityEnum.default('day'),
  compareWithPrevious: z.boolean().default(false),
});
export type AnalyticsRequest = z.infer<typeof AnalyticsRequestSchema>;
